import React, { useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Sparkles, Landmark, Receipt, HandCoins, FileCheck2, Inbox,
  Cloud, ShieldCheck, BookOpen, LifeBuoy, Zap, Search, X,
} from "lucide-react";
import { roleLabel } from "@/lib/roles";

const roleOverview = {
  Admin: "Full system access. Manage users, budgets, approvals, masters, migration, cron jobs and SLAs.",
  ProjectManager: "Own projects. Upload MoU for AI extraction, assign tasks, endorse team reimbursements.",
  Accounts: "Upload budgets, verify reimbursements, disburse advances and run salary import.",
  Approver: "Authorize budgets, approve reimbursements/advances, sign off Utilization Certificates.",
  Employee: "Submit expense reimbursements and advances, track your requests and tasks.",
  Auditor: "Read-only access to budgets, reports and audit log.",
};

const guides = [
  {
    id: "getting-started",
    icon: BookOpen,
    title: "Getting Started",
    keywords: "welcome onboarding first steps quickstart intro tour walkthrough new user projects budgets reimbursement mou upload",
    body: (
      <div className="space-y-3 text-sm text-slate-600 leading-relaxed">
        <p>Welcome to <span className="font-semibold text-slate-900">My RKF</span> — a compliance-first ERP for grant-funded projects.</p>
        <ol className="list-decimal ml-5 space-y-1">
          <li>Head to <b>Projects</b> and open one you're part of.</li>
          <li>On the <b>Logical Framework</b> tab, upload your project's LogFrame or Scope of Work — the AI parses Goal → Outcomes → Outputs → Activities for you to review.</li>
          <li>Under <b>Budgets</b>, upload your project budget in Excel.</li>
          <li>Submit a reimbursement or advance from the relevant page.</li>
          <li>Watch the <b>Pending on Me</b> inbox for anything awaiting your action.</li>
        </ol>
      </div>
    ),
  },
  {
    id: "mou-ai",
    icon: Sparkles,
    title: "AI Logical Framework Extraction",
    keywords: "logframe logical framework logframework mou sow scope of work ai claude sonnet extraction milestones activities indicators verification pdf docx txt upload parse preview review edit save goal outcome output",
    body: (
      <div className="space-y-2 text-sm text-slate-600 leading-relaxed">
        <p>Open a project → <b>Logical Framework</b> tab → click <b>Upload Logical Framework</b>.</p>
        <p>Claude Sonnet 4.6 reads up to 20,000 characters of your PDF, DOCX or TXT and returns Goal → Outcomes → Outputs → Activities with indicators and means of verification.</p>
        <p>You'll get a <b>preview dialog</b> — edit the goal, summary and any milestone (title, description, due offset, indicators), remove ones you don't need, then click <b>Save</b> to commit.</p>
      </div>
    ),
  },
  {
    id: "budgets",
    icon: Landmark,
    title: "Budgets (Budget V2)",
    keywords: "budget budgets sanctioned working excel upload heads sub-types category codified project head allocation",
    body: (role) => (role === "Admin" || role === "Accounts") ? (
      <div className="space-y-2 text-sm text-slate-600 leading-relaxed">
        <p>The codified Budget V2 tree: <b>Category → Standard Head → Project Head → Project Sub-Type → FY Allocation</b>.</p>
        <p><b>Sanctioned</b> amounts (the legal ceiling) are visible only to Super Admin and Accounts. Everyone else sees <b>Working</b> only.</p>
        <p>Upload from the <b>Budgets</b> page in Excel. Rows are name-resolved against the master list (exact → alias → fuzzy). Anything unmatched surfaces for review before commit.</p>
        <p>Every reimbursement, advance and expense charges a <b>Project Sub-Type</b> — its Working balance drops in real time.</p>
      </div>
    ) : (
      <div className="space-y-2 text-sm text-slate-600 leading-relaxed">
        <p>Each project has a codified <b>Budget</b> with Categories → Heads → Sub-Types.</p>
        <p>When you submit a reimbursement or advance, pick the sub-type it belongs to — its Working balance updates instantly.</p>
        <p>Sanctioned totals are visible only to Super Admin and Accounts.</p>
      </div>
    ),
  },
  {
    id: "reimbursements",
    icon: Receipt,
    title: "Reimbursements",
    keywords: "reimbursement reimbursements expense claim invoice payee tds submit pm endorse verify approve reject payment paid bulk approve late claim duplicate",
    body: (
      <div className="space-y-2 text-sm text-slate-600 leading-relaxed">
        <p>Submit an expense claim tied to a budget line. Flow: <b>Submitted → PM Endorsed → Accounts Verified → Approved → Paid</b>.</p>
        <p>Duplicate invoice numbers per payee are blocked. Late claims within the same FY need a reason; previous FY needs an Authorizer override.</p>
        <p>Approvers/Accounts can bulk-approve from the <b>Pending on Me</b> inbox.</p>
      </div>
    ),
  },
  {
    id: "advances",
    icon: HandCoins,
    title: "Advances",
    keywords: "advance advances imprest cash disburse settle refund outstanding ageing 30 days approve reject partially settled",
    body: (
      <div className="space-y-2 text-sm text-slate-600 leading-relaxed">
        <p>Request an advance for a purpose. Flow: <b>Requested → Approved → Disbursed → PartiallySettled → Settled</b>.</p>
        <p>Any advance older than 30 days blocks new advance requests until settled — this keeps ageing clean.</p>
        <p>Settle by linking one or more reimbursements + optional refund reference for unspent cash.</p>
      </div>
    ),
  },
  {
    id: "fund-uc",
    icon: FileCheck2,
    title: "Fund Receipts & GFR 12-A UC",
    keywords: "fund receipts grant tranche recovery 80g fcra utilization certificate uc gfr 12-a reconciliation freeze period pdf",
    body: (
      <div className="space-y-2 text-sm text-slate-600 leading-relaxed">
        <p>Record every grant tranche or recovery under <b>Fund Receipts</b>. Recoveries are netted against expenditure.</p>
        <p>Open <b>UC (GFR 12-A)</b> for a project → reconciliation runs first (unmapped lines, unsettled advances, approved-unpaid reimbursements). Fix any flags then approve.</p>
        <p>Approving with <b>freeze=true</b> closes the months so no one can post retroactively.</p>
      </div>
    ),
  },
  {
    id: "approvals",
    icon: Inbox,
    title: "Approvals Inbox & SLAs",
    keywords: "approvals inbox pending on me bulk approve inline verify sla overdue nag email department settings breach badge",
    body: (
      <div className="space-y-2 text-sm text-slate-600 leading-relaxed">
        <p>Everything waiting on you lands in <b>Pending on Me</b>. Bulk approve, inline-verify, or open the full detail.</p>
        <p>Overdue items get a red badge in the top-nav and a nag email once per day via the scheduled cron.</p>
        <p>Admins can set per-department SLAs under <b>SLA Settings</b>. Defaults are 3 days (endorse/verify), 5 days (approve), 7 days (disburse), 3 days (payment).</p>
      </div>
    ),
  },
  {
    id: "keka",
    icon: Cloud,
    title: "Keka HR & Salary Sync",
    keywords: "keka hr employees payroll salary csv import commit sync api cron nightly allocation 100 percent postings",
    body: (
      <div className="space-y-2 text-sm text-slate-600 leading-relaxed">
        <p>Employees can be imported from Keka CSV export or pulled live via the Keka API (see <b>Keka Sync</b>).</p>
        <p>Nightly cron sync keeps the employee list fresh. Check the <b>Cron Runs</b> dashboard for last-run status.</p>
        <p>Salary import: upload the monthly CSV, review the preview, then commit rows where allocation totals to 100%.</p>
      </div>
    ),
  },
  {
    id: "roles",
    icon: ShieldCheck,
    title: "Roles & Permissions",
    keywords: "roles permissions admin approver authorizer accounts project manager employee auditor read only access rights super admin",
    body: (
      <div className="space-y-2 text-sm text-slate-600 leading-relaxed">
        <ul className="list-disc ml-5 space-y-1">
          <li><b>Admin</b>: full access</li>
          <li><b>Approver (Authorizer)</b>: locks budgets, approves reimbursements/advances, signs UC</li>
          <li><b>Accounts</b>: uploads budget, verifies reimbursements, disburses, salary import</li>
          <li><b>ProjectManager</b>: owns projects, endorses reimbursements, assigns tasks</li>
          <li><b>Employee</b>: submits requests, sees only their own</li>
          <li><b>Auditor</b>: read-only across the board</li>
        </ul>
      </div>
    ),
  },
  {
    id: "support",
    icon: LifeBuoy,
    title: "Need Help?",
    keywords: "help support contact administrator bug report feature request audit log troubleshoot",
    body: (
      <div className="space-y-2 text-sm text-slate-600 leading-relaxed">
        <p>For a walkthrough, ask your <b>administrator</b> — they can grant roles and reset departmental SLAs.</p>
        <p>Report bugs or feature requests to your project sponsor with a screenshot and the exact time it happened; the <b>Audit Log</b> (Admin only) usually has the trail.</p>
      </div>
    ),
  },
];

export default function Help() {
  const { user } = useAuth();
  const yourRole = user?.role;
  const [query, setQuery] = useState("");

  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return guides;
    return guides.filter((g) =>
      g.title.toLowerCase().includes(q) || (g.keywords || "").toLowerCase().includes(q)
    );
  }, [q]);

  // Auto-expand: all matches when searching, else just "getting-started"
  const openValues = q ? filtered.map((g) => g.id) : ["getting-started"];

  return (
    <div className="p-6 lg:p-10 max-w-4xl">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">Documentation</div>
        <h1 className="font-display text-3xl font-extrabold mt-1" data-testid="help-title">Help &amp; Guide</h1>
        <p className="text-sm text-slate-500 mt-1 max-w-2xl">
          Quick reference for every workflow in My RKF. Bookmark this page — the sidebar link stays visible to everyone.
        </p>
      </div>

      {/* Search */}
      <div className="relative mb-5">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search — try 'excel', 'sla', 'roles', 'mou'…"
          className="pl-9 pr-9"
          data-testid="help-search-input"
        />
        {query && (
          <button
            onClick={() => setQuery("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100 text-slate-500"
            aria-label="Clear search"
            data-testid="help-search-clear"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Your role */}
      <Card className="mb-6 border-blue-100 bg-blue-50/40" data-testid="your-role-card">
        <CardContent className="p-5 flex items-start gap-4">
          <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shrink-0 border border-blue-200">
            <Zap className="w-5 h-5 text-blue-600" />
          </div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Your Role</div>
            <div className="flex items-center gap-2 mt-1">
              <div className="font-display font-bold text-base">{roleLabel(yourRole)}</div>
              <Badge variant="secondary" className="text-[10px]">{yourRole}</Badge>
            </div>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">{roleOverview[yourRole] || "Contact your administrator for role details."}</p>
          </div>
        </CardContent>
      </Card>

      {/* Guides */}
      {filtered.length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center text-sm text-slate-500" data-testid="help-empty">
            No guide matches <b className="text-slate-700">"{query}"</b>. Try a broader keyword like "budget" or "approval".
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Accordion
              key={q}
              type="multiple"
              defaultValue={openValues}
              className="w-full"
            >
              {filtered.map((g) => {
                // Non-admins see plain "Budgets" for the codified module too
                const displayTitle = (yourRole !== "Admin" && g.id === "budgets") ? "Budgets" : g.title;
                return (
                <AccordionItem key={g.id} value={g.id} className="px-5" data-testid={`guide-${g.id}`}>
                  <AccordionTrigger className="hover:no-underline py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-md bg-slate-100 flex items-center justify-center">
                        <g.icon className="w-4 h-4 text-slate-700" />
                      </div>
                      <span className="font-semibold text-slate-900">{displayTitle}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 pl-11">
                    {typeof g.body === "function" ? g.body(yourRole) : g.body}
                  </AccordionContent>
                </AccordionItem>
                );
              })}
            </Accordion>
          </CardContent>
        </Card>
      )}

      {q && (
        <div className="mt-3 text-xs text-slate-500 font-mono" data-testid="help-result-count">
          {filtered.length} match{filtered.length === 1 ? "" : "es"} for "{query}"
        </div>
      )}

      <div className="mt-6 text-xs text-slate-500 font-mono">
        My RKF v1.4 · Compliance-first · Last updated Feb 2026
      </div>
    </div>
  );
}
